import type { RxDocument } from 'rxdb';

import { SourceInput } from '@/components/source-input';
import { InlineEditActions } from '@/components/inline-edit-actions';

import { useUserSourceCreate, useUserSourcePatch } from '@/hooks/rxdb-models';

import type { UserSource } from '@flintmint/core-shared/rxdb';
import type { RenderAlert } from '@flintmint/web-shared/hooks/use-close-confirmation';

import styles from '../cortex-preview-modal.module.scss';

export const CortexPreviewActions = (props: CortexPreviewActionsProps) => {
  const { onClose, content, onCancel, userSource, inEditMode, noteVariant, setInEditMode, onReplaceWithNew, ...rest } = props;

  const { mutate: createUserSource } = useUserSourceCreate();
  const { mutate: patchUserSource } = useUserSourcePatch();

  const handleSave = () => {
    return patchUserSource(
      userSource,
      {
        content,
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  const handleSaveAsNew = (name: string, replaceItemInTheNote: boolean) => {
    return createUserSource(
      {
        name,
        content,
        source: userSource!.source,
        sourceType: userSource!.sourceType,
      },
      {
        onSuccess: (res) => {
          replaceItemInTheNote && onReplaceWithNew(userSource.id, res.id);
          onClose();
        },
      }
    );
  };

  return (
    <div className={styles.cortexActions}>
      <SourceInput editable={inEditMode} showReSync={inEditMode} userSource={userSource} onCreateNew={onClose} />
      <InlineEditActions
        saveAsNew
        type="userSources"
        onCancel={onCancel}
        onSave={handleSave}
        editing={inEditMode}
        processing={userSource.syncStatus !== 'ready'}
        noteVariant={noteVariant}
        onSaveAsNew={handleSaveAsNew}
        documentName={userSource.name}
        onStartEditing={() => setInEditMode(true)}
        {...rest}
      />
    </div>
  );
};

interface CortexPreviewActionsProps {
  content: string;
  onClose: () => void;
  inEditMode: boolean;
  noteVariant?: string;
  onCancel: () => void;
  renderAlert: RenderAlert;
  isAttachedToDoc?: boolean;
  saveBlockedByReview: boolean;
  hasUnsavedChanges: boolean;
  isCloseConfirmationOpen: boolean;
  userSource: RxDocument<UserSource>;
  setInEditMode: (value: boolean) => void;
  onReplaceWithNew: (id: string, newId: string) => void;
}
